/* eslint-disable @typescript-eslint/no-explicit-any */
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {NavigationEnd} from '@angular/router';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {PlatformStorage} from '@app/core/tokens/platform-storage.token';
import {OpenIdConfiguration} from 'angular-auth-oidc-client';
import {Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {IAuthRequest} from '../models/auth-request.model';
import {IAuthResponse} from '../models/auth-response.model';
import {IResetPasswordRequest} from '../models/reset-password.model';
import {TokenUtils} from '../utils/token.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthConfigService {
  private readonly _httpClient = inject(HttpClient);
  private readonly storage = inject(PlatformStorage);
  private readonly localStorageService = inject(LocalStorageService);

  private readonly getAccessTokenUrl = `${environment.api.authUrl}/${environment.api.accessToken}`;

  getConfig(): OpenIdConfiguration {
    return {
      postLoginRoute: environment.oidcConfig.postLoginRoute,
      unauthorizedRoute: environment.oidcConfig.unauthorizedRoute,
      authority: environment.api.authUrl,
      redirectUrl: `${window.location.origin}`,
      postLogoutRedirectUri: `${window.location.origin}${environment.oidcConfig.postLogoutRoute}`,
      clientId: environment.oidcConfig.clientId,
      scope: environment.oidcConfig.scope,
      responseType: environment.oidcConfig.responseType,
      silentRenew: environment.oidcConfig.silentRenew,
      useRefreshToken: environment.oidcConfig.useRefreshToken,
      logLevel: +environment.oidcConfig.logLevel,
      maxIdTokenIatOffsetAllowedInSeconds: environment.oidcConfig.maxIdTokenIatOffsetAllowedInSeconds,
    };
  }
  getPreAuth(authRequest: IAuthRequest): Observable<IAuthResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('client_id', authRequest.client_id);
    urlSearchParams.set('grant_type', authRequest.grant_type);
    urlSearchParams.set('client_secret', authRequest.client_secret);
    urlSearchParams.set('scope', authRequest.scope);
    return this._httpClient.post<IAuthResponse>(this.getAccessTokenUrl, encodeURI(urlSearchParams.toString()), {
      headers,
    });
  }

  isTokenValid(): boolean {
    const token = this.storage.getItem<string>(StorageKeys.accessToken);
    const isValid = TokenUtils.isTokenValid(token ?? '');
    if (!isValid) {
      this.localStorageService.removeItem(StorageKeys.selectedOrganization);
    }
    return isValid;
  }

  getAccessToken(): string {
    const token = this.storage.getItem<string>(StorageKeys.accessToken);
    return token ?? '';
  }

  getAccountConfirmation(email: string, token: string): Observable<string> {
    const getAccountConfirmationUrl = `${environment.api.baseUrl}/${environment.api.user.getAccountConfirmation}`;

    const params = new HttpParams().set('email', email).set('token', token);

    return this._httpClient.post(getAccountConfirmationUrl, null, {
      params,
      responseType: 'text',
    });
  }

  resetPassword(request: IResetPasswordRequest): Observable<string> {
    const getResetPasswordUrl = `${environment.api.baseUrl}/${environment.api.user.resetPassword}`;

    return this._httpClient.post(getResetPasswordUrl, request, {
      responseType: 'text',
    });
  }

  forgotPassword(email: string): Observable<string> {
    const getForgotPasswordUrl = `${environment.api.baseUrl}/${environment.api.user.forgotPassword}`;

    const params = new HttpParams().set('email', email);

    return this._httpClient.post(getForgotPasswordUrl, null, {
      params,
      responseType: 'text',
    });
  }

  checkPreAuthUrl(event: NavigationEnd): boolean {
    const preUrlsToMatch: string[] = ['account-confirmation', 'reset-password'];
    return preUrlsToMatch.some(url => event.url?.includes(url));
  }

  checkAccountConfirmationUrl(event: NavigationEnd): boolean {
    const preUrlsToMatch: string[] = ['account-confirmation'];
    return preUrlsToMatch.some(url => event.url?.includes(url));
  }

  checkThankYouUrl(event: NavigationEnd): boolean {
    const preUrlsToMatch: string[] = ['thank-you'];
    return preUrlsToMatch.some(url => event.url?.includes(url));
  }

  changePassword(oldPassword: string, newPassword: string): Observable<string> {
    const getChangePasswordUrl = `${environment.api.baseUrl}/${environment.api.user.changePassword}`;

    const passwordBody = {
      currentPassword: oldPassword,
      newPassword: newPassword,
    };

    return this._httpClient.post(getChangePasswordUrl, passwordBody, {
      responseType: 'text',
    });
  }
}
